import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data)
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: (limit = 10) => api.get(`/dashboard/recent-activity?limit=${limit}`),
  getOrdersSummary: () => api.get('/dashboard/orders-summary'),
  getDeliveriesByDay: (days = 7) => api.get(`/dashboard/deliveries-by-day?days=${days}`),
  getRevenueByDay: (days = 7) => api.get(`/dashboard/revenue-by-day?days=${days}`)
};

// Customers
export const customersAPI = {
  list: (params = {}) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  count: (params = {}) => api.get('/customers/count', { params }),
  addSite: (customerId, site) => api.post(`/customers/${customerId}/sites`, site),
  updateSite: (customerId, siteId, site) => api.put(`/customers/${customerId}/sites/${siteId}`, site),
  deleteSite: (customerId, siteId) => api.delete(`/customers/${customerId}/sites/${siteId}`),
  updatePricing: (customerId, pricing) => api.put(`/customers/${customerId}/pricing`, pricing),
  updateCredit: (customerId, credit) => api.put(`/customers/${customerId}/credit`, credit)
};

// Products
export const productsAPI = {
  list: (params = {}) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`)
};

// Depots
export const depotsAPI = {
  list: (params = {}) => api.get('/depots', { params }),
  get: (id) => api.get(`/depots/${id}`),
  create: (data) => api.post('/depots', data),
  delete: (id) => api.delete(`/depots/${id}`)
};

// Vehicles
export const vehiclesAPI = {
  list: (params = {}) => api.get('/vehicles', { params }),
  get: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  delete: (id) => api.delete(`/vehicles/${id}`)
};

// Drivers
export const driversAPI = {
  list: (params = {}) => api.get('/drivers', { params }),
  get: (id) => api.get(`/drivers/${id}`),
  create: (data) => api.post('/drivers', data),
  delete: (id) => api.delete(`/drivers/${id}`)
};

// Orders
export const ordersAPI = {
  list: (params = {}) => api.get('/orders', { params }),
  get: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  count: (params = {}) => api.get('/orders/count', { params }),
  confirm: (id) => api.post(`/orders/${id}/confirm`),
  getUnplanned: (params = {}) => api.get('/orders/unplanned', { params }),
  calculatePrice: (customerId, productId, quantity) => 
    api.post(`/orders/calculate-price?customer_id=${customerId}&product_id=${productId}&quantity=${quantity}`)
};

// Loads
export const loadsAPI = {
  list: (params = {}) => api.get('/loads', { params }),
  get: (id) => api.get(`/loads/${id}`),
  create: (data) => api.post('/loads', data),
  update: (id, data) => api.put(`/loads/${id}`, data),
  delete: (id) => api.delete(`/loads/${id}`),
  dispatch: (id) => api.post(`/loads/${id}/dispatch`),
  addOrder: (loadId, orderId, sequence) => 
    api.post(`/loads/${loadId}/add-order?order_id=${orderId}&sequence=${sequence}`),
  getToday: () => api.get('/loads/today'),
  getDriverLoads: (driverId) => api.get(`/loads/driver/${driverId}`)
};

// Deliveries
export const deliveriesAPI = {
  list: (params = {}) => api.get('/deliveries', { params }),
  get: (id) => api.get(`/deliveries/${id}`),
  create: (data) => api.post('/deliveries', data),
  arrive: (id) => api.post(`/deliveries/${id}/arrive`),
  start: (id) => api.post(`/deliveries/${id}/start`),
  submitSafetyCheck: (id, checks) => api.post(`/deliveries/${id}/safety-check`, checks),
  updateLines: (id, lines) => api.put(`/deliveries/${id}/lines`, lines),
  captureSignature: (id, signature) => api.post(`/deliveries/${id}/signature`, signature),
  addPhoto: (id, photo) => api.post(`/deliveries/${id}/photo`, photo),
  complete: (id, notes) => api.post(`/deliveries/${id}/complete${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`),
  fail: (id, reason) => api.post(`/deliveries/${id}/fail?reason=${encodeURIComponent(reason)}`)
};

// Invoices
export const invoicesAPI = {
  list: (params = {}) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  delete: (id) => api.delete(`/invoices/${id}`),
  generateFromDelivery: (deliveryId) => api.post(`/invoices/generate-from-delivery/${deliveryId}`),
  issue: (id) => api.post(`/invoices/${id}/issue`),
  recordPayment: (id, amount) => api.post(`/invoices/${id}/payment?amount=${amount}`),
  getCustomerStatements: (customerId) => api.get(`/invoices/customer/${customerId}/statements`)
};

// Admin
export const adminAPI = {
  listUsers: (params = {}) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getAuditLogs: (params = {}) => api.get('/admin/audit-logs', { params }),
  listPriceRules: (params = {}) => api.get('/admin/price-rules', { params }),
  createPriceRule: (data) => api.post('/admin/price-rules', data),
  deletePriceRule: (id) => api.delete(`/admin/price-rules/${id}`),
  getSystemStats: () => api.get('/admin/system-stats')
};

export default api;
